import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet } from '../utils/api';
import { calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import Pagination from './Pagination';

// admin-app has no lucide-react dependency (and none may be added), so these
// small inline SVGs stand in for the icons the original dental-clinic-app
// screen imports from lucide-react. Same paths/viewBox as lucide's icon set.
const IconAlertTriangle = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconPill = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </svg>
);

// This screen is READ-ONLY on purpose.
//
// The admin-console login token carries {admin_id, email, name, status,
// is_super_admin} and no `role`, while the write routes on /api/medications sit
// behind authorizeAdmin, which requires role === 'admin'. Every POST/PUT/DELETE
// from here would answer 403. Rather than show buttons that always fail — or
// widen the admin token, which would retroactively unlock every other
// authorizeAdmin route in the API — the console browses the catalog across
// branches and the clinic app owns the editing.
const Medications = () => {
  const { print_lang, currentLanguage } = useLanguage();

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');

  const [medications, setMedications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    apiGet('/api/admin/branches')
      .then((res) => {
        const list = res?.data || res || [];
        setBranches(list);
        if (list.length > 0 && !branchId) setBranchId(String(list[0].branch_id));
      })
      .catch(() => setBranches([]));
    // Runs once; the selector drives every later fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedBranch = branches.find((b) => String(b.branch_id) === String(branchId));
  const branchVatRate = branchRate(selectedBranch);

  const catName = (row) =>
    currentLanguage === 'th'
      ? (row.category_name_th || row.category_name_en || '-')
      : (row.category_name_en || '-');

  const fetchMedications = useCallback(async () => {
    if (!branchId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        branch_id: branchId, page: currentPage, limit: pageSize, status: 'all',
      });
      if (searchTerm) params.set('search', searchTerm);
      if (categoryFilter) params.set('category_id', categoryFilter);
      const res = await apiGet(`/api/medications?${params}`);
      setMedications(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalCount(res?.pagination?.totalCount || 0);
    } catch (err) {
      console.error(err.message || 'Failed to load medications', err);
      setMedications([]);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, currentPage, pageSize, searchTerm, categoryFilter]);

  useEffect(() => { fetchMedications(); }, [fetchMedications]);

  useEffect(() => {
    if (!branchId) return;
    apiGet('/api/medications/categories')
      .then((res) => setCategories(res?.data || []))
      .catch(() => setCategories([]));
    apiGet(`/api/medications/low-stock?branch_id=${branchId}`)
      .then((res) => setLowStockCount((res?.data || []).length))
      .catch(() => setLowStockCount(0));
  }, [branchId, medications.length]);

  const priceInclVat = (row) => {
    const rate = resolveVatRate({
      vatMode: row.vat_mode, itemRate: row.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: 1, unit_price: row.price_per_unit, vat_mode: row.vat_mode, vat_rate: rate,
    }).total;
  };

  // Within 90 days, or already past.
  const expiringSoon = (d) => {
    if (!d) return false;
    const limit = new Date();
    limit.setDate(limit.getDate() + 90);
    return new Date(d) <= limit;
  };

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1 className="page-title">{print_lang('medications', 'Medications')}</h1>
          {lowStockCount > 0 && (
            <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <IconAlertTriangle size={14} />
              {lowStockCount} {print_lang('low_stock', 'Low Stock')}
            </span>
          )}
        </div>
      </div>

      <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
        {print_lang('catalog.readOnly', 'View only — manage the catalog in the clinic app')}
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="grid-3">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('search_medications', 'Search medications...')}</label>
            <input
              type="text"
              className="form-input"
              placeholder={print_lang('search_medications', 'Search medications...')}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('category', 'Category')}</label>
            <select className="form-select" value={categoryFilter}
                    onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">{print_lang('all_categories', 'All Categories')}</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('branch.name', 'Branch')}</label>
            <select
              className="form-select"
              value={branchId}
              onChange={(e) => { setBranchId(e.target.value); setCurrentPage(1); }}
            >
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner" />
          </div>
        ) : medications.length === 0 ? (
          <div className="empty-state">
            <IconPill size={40} />
            <h3 className="empty-state-title">{print_lang('no_medications', 'No medications found')}</h3>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>{print_lang('medication', 'Medication')}</th>
                    <th>{print_lang('category', 'Category')}</th>
                    <th>{print_lang('dosage_form', 'Dosage Form')}</th>
                    <th>{print_lang('unit', 'Unit')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('price_per_unit', 'Price per Unit')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('price_incl_vat', 'Price incl. VAT')}</th>
                    <th style={{ textAlign: 'right' }}>{print_lang('stock_quantity', 'Stock')}</th>
                    <th>{print_lang('expiry_date', 'Expiry Date')}</th>
                    <th>{print_lang('status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {medications.map((m) => {
                    const low = Number(m.stock_quantity) <= Number(m.min_stock_level);
                    const exp = expiringSoon(m.expiry_date);
                    return (
                      <tr key={m.medication_id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {m.name}
                            {m.strength ? ` ${m.strength}` : ''}
                          </div>
                          {m.generic_name && (
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{m.generic_name}</div>
                          )}
                        </td>
                        <td>{catName(m)}</td>
                        <td>{print_lang(m.dosage_form, m.dosage_form)}</td>
                        <td>{m.unit}</td>
                        <td style={{ textAlign: 'right' }}>{Number(m.price_per_unit).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{priceInclVat(m).toFixed(2)}</td>
                        <td style={{ textAlign: 'right', color: low ? '#dc2626' : undefined, fontWeight: low ? 600 : undefined }}>
                          {Number(m.stock_quantity).toFixed(0)}
                        </td>
                        <td style={{ color: exp ? '#ea580c' : undefined, fontWeight: exp ? 500 : undefined }}>
                          {m.expiry_date ? String(m.expiry_date).slice(0, 10) : '-'}
                        </td>
                        <td>
                          <span className={`badge ${m.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                            {print_lang(m.status, m.status)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Medications;
